import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DomainService } from '../apis/domain.service';
import { WindowService } from '../local/window.service';
import { storageKeys } from 'src/app/share/configs';

@Injectable({
  providedIn: 'root',
})
export class StoreContextGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly firstPartyHosts = new Set([
    'uorder.io',
    'www.uorder.io',
    'localhost',
  ]);

  constructor(
    private domainServe: DomainService,
    private windowServe: WindowService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    // Case 1: route already has :restaurantId (e.g. /store/227)
    // Just save it and let the route activate normally — NO redirect.
    const routeStoreId =
      route.params['restaurantId'] ||
      route.parent?.params['restaurantId'];
    if (routeStoreId) {
      this.windowServe.setLocalStorage(storageKeys.store, routeStoreId);
      return true;
    }

    // Case 2: first-party host at root (no storeId in URL) → go to /home
    const hostname = window.location.hostname;
    if (this.isFirstPartyHost(hostname)) {
      return this.router.createUrlTree(['/home']);
    }

    // Case 3: tenant/custom domain at root → resolve via API then redirect to /store/:id
    const secure = window.location.protocol === 'https:';
    return this.domainServe
      .postDomain({ Domain: hostname, secure })
      .pipe(
        switchMap((res) => {
          const domain = this.extractDomain(res);
          if (domain?.store_id) {
            this.windowServe.setLocalStorage(
              storageKeys.store,
              domain.store_id.toString()
            );
            if (domain.merchant_id !== undefined && domain.merchant_id !== null) {
              this.windowServe.setLocalStorage(
                storageKeys.merchant,
                domain.merchant_id.toString()
              );
            }
            return of(
              this.router.createUrlTree(['/store', domain.store_id.toString()])
            );
          }
          // Fallback: try legacy GET endpoint
          return this.domainServe.domain(hostname).pipe(
            map((fallbackRes) => {
              const d = this.extractDomain(fallbackRes);
              if (!d?.store_id) {
                return this.router.createUrlTree(['/home']);
              }
              this.windowServe.setLocalStorage(
                storageKeys.store,
                d.store_id.toString()
              );
              if (d.merchant_id !== undefined && d.merchant_id !== null) {
                this.windowServe.setLocalStorage(
                  storageKeys.merchant,
                  d.merchant_id.toString()
                );
              }
              return this.router.createUrlTree([
                '/store',
                d.store_id.toString(),
              ]);
            }),
            catchError(() => of(this.router.createUrlTree(['/home'])))
          );
        }),
        catchError(() => of(this.router.createUrlTree(['/home'])))
      );
  }

  private isFirstPartyHost(hostname: string): boolean {
    return (
      this.firstPartyHosts.has(hostname) || hostname.startsWith('localhost:')
    );
  }

  private extractDomain(res: any): {
    store_id?: number | string;
    merchant_id?: number | string;
  } | null {
    if (!res?.data) {
      return null;
    }
    if (res.data.item) {
      return res.data.item;
    }
    return res.data;
  }
}
